/**
 * Billing page building blocks — one section per subscription state.
 * Visuals ported from `design-handoff/tabvault-ds/ui_kits/vault/Vault Errors
 * Billing.dc.html` (F1 payment-failed banner + dunning card, F2 cancel /
 * retention modal) and the Pricing kit for the Free-vs-Pro mini table.
 * Tokens only (`var(--…)`); interactive atoms come from @vault/ui.
 */
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Badge, Button, Icon, Modal, Skeleton } from '@vault/ui';
import type { Subscription } from '../../api/client.js';
import { formatDate } from '../_vault/shared.js';

export type Interval = 'monthly' | 'yearly';

export interface ProPricing {
  monthlyUsd: number;
  yearlyUsd: number;
}

// ── helpers ────────────────────────────────────────────────────────────────
/** Sentence-case date for body copy — "Apr 12, 2027". */
export function prettyDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function brandLabel(brand: string | null): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function discountPct(pricing: ProPricing): number {
  return Math.round((1 - pricing.yearlyUsd / (pricing.monthlyUsd * 12)) * 100);
}

// ── shared shells (sibling of SettingsPage) ────────────────────────────────
export function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <div style={sectionShell}>
      <div style={sectionHead}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>{title}</span>
        {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

/** Credit-card glyph — geometry copied verbatim from the F1 kit specimen. */
function CardGlyph() {
  return (
    <svg width={15} height={15} viewBox="0 0 16 16" fill="none" stroke="var(--text-3)" strokeWidth={1.5} strokeLinecap="round" aria-hidden>
      <rect x="1.8" y="3.5" width="12.4" height="9" rx="1.8" />
      <path d="M2 6.5h12" />
    </svg>
  );
}

/** Card-on-file row — "{Brand} ···· {last4}", optional DECLINED badge. */
export function CardOnFile({
  brand,
  last4,
  declined = false,
  right,
}: {
  brand: string | null;
  last4: string | null;
  declined?: boolean;
  right?: ReactNode;
}) {
  return (
    <div style={cardRow}>
      <CardGlyph />
      {last4 ? (
        <>
          <span style={{ fontSize: 12.5, fontWeight: 500 }}>{brandLabel(brand)}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-2)' }}>···· {last4}</span>
        </>
      ) : (
        <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No card on file</span>
      )}
      {declined && <Badge tone="danger">Declined</Badge>}
      {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
    </div>
  );
}

/** Monthly / yearly pill toggle — same idiom as PricingPage. */
export function IntervalToggle({
  value,
  onChange,
  pricing,
}: {
  value: Interval;
  onChange: (v: Interval) => void;
  pricing: ProPricing;
}) {
  return (
    <span style={{ display: 'inline-flex', border: '1px solid var(--line-strong)', borderRadius: 999, padding: 2, gap: 2 }}>
      <IntervalTab active={value === 'monthly'} onClick={() => onChange('monthly')}>
        Monthly ${pricing.monthlyUsd}
      </IntervalTab>
      <IntervalTab active={value === 'yearly'} onClick={() => onChange('yearly')}>
        Yearly ${pricing.yearlyUsd}{' '}
        <span style={{ color: 'var(--success)', fontFamily: 'var(--font-mono)', fontSize: 9 }}>−{discountPct(pricing)}%</span>
      </IntervalTab>
    </span>
  );
}

function IntervalTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: '6px 14px',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: 11.5,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--text-1)' : 'var(--text-2)',
        background: active ? 'var(--surface-3)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ── dunning banner (F1 top strip) ──────────────────────────────────────────
export function DunningBanner({
  graceEndsAt,
  busy,
  onUpdateCard,
}: {
  graceEndsAt: number | null;
  busy: boolean;
  onUpdateCard: () => void;
}) {
  return (
    <div style={banner} role="alert">
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--danger)', whiteSpace: 'nowrap' }}>Payment didn’t go through</span>
      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
        {graceEndsAt
          ? `Pro stays active while we retry — grace period ends ${prettyDate(graceEndsAt)}.`
          : 'Pro stays active while we retry.'}
      </span>
      <button type="button" style={bannerLink} onClick={busy ? undefined : onUpdateCard}>
        Update card →
      </button>
    </div>
  );
}

// ── ACTIVE — subscription card + payment method management ────────────────
export function ActiveSection({
  sub,
  pricing,
  portalBusy,
  onPortal,
  onCancel,
}: {
  sub: Subscription;
  pricing: ProPricing;
  portalBusy: boolean;
  onPortal: () => void;
  onCancel: () => void;
}) {
  const billedLine =
    sub.interval === 'yearly'
      ? `YEARLY · $${pricing.yearlyUsd} / YEAR`
      : sub.interval === 'monthly'
        ? `MONTHLY · $${pricing.monthlyUsd} / MO`
        : 'PRO PLAN';
  const switchLabel =
    sub.interval === 'yearly'
      ? 'Switch to monthly'
      : sub.interval === 'monthly'
        ? `Switch to yearly (−${discountPct(pricing)}%)`
        : 'Change billing interval';

  return (
    <Section title="SUBSCRIPTION" right={<Badge tone="pro">PRO</Badge>}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>
            {sub.renewsAt ? `Pro · renews ${prettyDate(sub.renewsAt)}` : 'Pro · active'}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>{billedLine}</span>
        </span>
        <span style={{ marginLeft: 'auto' }}>
          <Button variant="ghost" size="sm" loading={portalBusy} onClick={onPortal}>
            Manage billing
          </Button>
        </span>
      </div>

      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
        <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.12em', color: 'var(--text-3)', marginBottom: 8 }}>
          PAYMENT METHOD
        </span>
        <CardOnFile
          brand={sub.cardBrand}
          last4={sub.cardLast4}
          right={
            <Button variant="ghost" size="sm" loading={portalBusy} onClick={onPortal}>
              Update card
            </Button>
          }
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
        <Button variant="ghost" size="sm" loading={portalBusy} onClick={onPortal}>
          {switchLabel}
        </Button>
        <button type="button" style={cancelLink} onClick={onCancel}>
          Cancel Pro
        </button>
      </div>
      <div style={{ padding: '0 16px 14px' }}>
        <span style={monoFootnote}>CANCEL ANYTIME · EXPORT YOUR DATA WHENEVER · LOCAL SAVES ARE NEVER LOCKED</span>
      </div>
    </Section>
  );
}

// ── PAST DUE — dunning card (F1) ───────────────────────────────────────────
export function PastDueSection({
  sub,
  busy,
  onPortal,
}: {
  sub: Subscription;
  busy: boolean;
  onPortal: () => void;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ ...stateCard, borderColor: 'var(--danger-line)' }}>
        <span style={stateTitle}>Update payment method</span>
        <span style={stateBody}>
          Your card ending{' '}
          <b style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-1)' }}>··{sub.cardLast4 ?? '····'}</b> was
          declined. Nothing is lost — your vault and Pro features keep working during the grace period.
        </span>
        <CardOnFile brand={sub.cardBrand} last4={sub.cardLast4} declined />
        <span style={stateBody}>
          If the charge keeps failing, your account moves to Free when the grace period ends — Pro features pause,
          local saves stay unlimited, and <b style={{ color: 'var(--text-1)' }}>nothing is deleted</b>.
        </span>
        <Button fullWidth loading={busy} onClick={onPortal}>
          Update payment method
        </Button>
        <span style={{ ...monoFootnote, textAlign: 'center' }}>
          {sub.renewsAt ? `GRACE UNTIL ${formatDate(sub.renewsAt)}` : 'GRACE PERIOD ACTIVE'} · THEN FREE PLAN, DATA UNTOUCHED
        </span>
      </div>
    </div>
  );
}

// ── CANCELLED — win-back card ──────────────────────────────────────────────
export function CancelledSection({
  sub,
  pricing,
  interval,
  onInterval,
  busy,
  onReactivate,
  checkoutSoon,
  checkoutFailed,
}: {
  sub: Subscription;
  pricing: ProPricing;
  interval: Interval;
  onInterval: (v: Interval) => void;
  busy: boolean;
  onReactivate: () => void;
  checkoutSoon: boolean;
  checkoutFailed: boolean;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ ...stateCard, borderColor: 'var(--line-primary)', background: 'linear-gradient(165deg, rgba(59,107,255,.14), transparent 50%), var(--surface-1)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Icon name="sparkle" size={15} color="var(--accent-ink)" />
          <span style={stateTitle}>{sub.renewsAt ? `Your Pro ended ${prettyDate(sub.renewsAt)}` : 'Your Pro has ended'}</span>
        </span>
        <span style={stateBody}>
          Reactivate anytime — your vault is intact. Until then: local saves stay unlimited, sync drops to 1 browser,
          and AI search and collections pause. <b style={{ color: 'var(--text-1)' }}>Nothing was deleted.</b>
        </span>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IntervalToggle value={interval} onChange={onInterval} pricing={pricing} />
        </div>
        <Button fullWidth loading={busy} onClick={onReactivate}>
          Reactivate Pro
        </Button>
        {checkoutSoon && <CheckoutSoonNote />}
        {checkoutFailed && <CheckoutFailedNote />}
        <span style={{ ...monoFootnote, textAlign: 'center' }}>YOUR VAULT IS INTACT · PICK UP RIGHT WHERE YOU LEFT OFF</span>
      </div>
    </div>
  );
}

// ── FREE — mini Free-vs-Pro table + upgrade CTA ────────────────────────────
const FREE_FEATURES: ReadonlyArray<{ label: string; muted?: boolean }> = [
  { label: 'Unlimited local saves' },
  { label: '1 browser synced to cloud' },
  { label: 'Title & URL search' },
  { label: 'Full-text & AI search', muted: true },
  { label: 'AI collections', muted: true },
];
const PRO_FEATURES: readonly string[] = [
  'All browsers, all devices synced',
  'Full-text + AI semantic search',
  'AI collections that sort themselves',
  'Version history of saved pages',
  'Priority support',
];

export function FreeSection({
  pricing,
  interval,
  onInterval,
  busy,
  onUpgrade,
  checkoutSoon,
  checkoutFailed,
}: {
  pricing: ProPricing;
  interval: Interval;
  onInterval: (v: Interval) => void;
  busy: boolean;
  onUpgrade: () => void;
  checkoutSoon: boolean;
  checkoutFailed: boolean;
}) {
  const perMonth = interval === 'yearly' ? Math.round(pricing.yearlyUsd / 12) : pricing.monthlyUsd;
  return (
    <Section title="YOUR PLAN" right={<Badge>FREE</Badge>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16 }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>You’re on Free</span>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Free stays generous. Pro connects everything.</span>
        </span>

        <div className="tvbl-plans" style={{ display: 'flex', gap: 12 }}>
          {/* Free */}
          <div style={{ ...miniPlan, background: 'var(--bg)', border: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={miniPlanName}>Free</span>
              <span style={yourPlanTag}>YOUR PLAN</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={miniPrice}>$0</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>FOREVER</span>
            </div>
            <ul style={featureList}>
              {FREE_FEATURES.map((f) => (
                <FeatureRow key={f.label} muted={f.muted}>{f.label}</FeatureRow>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div style={{ ...miniPlan, border: '1px solid var(--line-primary)', background: 'linear-gradient(165deg, rgba(24,24,27,.04), transparent 50%), var(--surface-1)' }}>
            <span style={miniPlanName}>Pro</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={miniPrice}>${perMonth}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>
                {interval === 'yearly' ? `/MO · BILLED $${pricing.yearlyUsd} YEARLY` : '/MO'}
              </span>
            </div>
            <ul style={featureList}>
              {PRO_FEATURES.map((label) => (
                <FeatureRow key={label} pro>{label}</FeatureRow>
              ))}
            </ul>
          </div>
        </div>

        <div className="tvbl-cta" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <IntervalToggle value={interval} onChange={onInterval} pricing={pricing} />
          <span style={{ marginLeft: 'auto' }}>
            <Button loading={busy} onClick={onUpgrade} icon={<Icon name="sparkle" size={13} color="currentColor" />}>
              Upgrade to Pro
            </Button>
          </span>
        </div>
        {checkoutSoon && <CheckoutSoonNote />}
        {checkoutFailed && <CheckoutFailedNote />}
        <span style={{ ...monoFootnote, textAlign: 'center' }}>CANCEL ANYTIME · EXPORT YOUR DATA WHENEVER · LOCAL SAVES ARE NEVER LOCKED</span>
      </div>
    </Section>
  );
}

function FeatureRow({ children, muted, pro }: { children: ReactNode; muted?: boolean; pro?: boolean }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: pro ? 500 : 400, color: muted ? 'var(--text-3)' : 'var(--text-1)' }}>
      {muted ? (
        <Icon name="minus" size={11} color="currentColor" />
      ) : (
        <Icon name="check" size={11} color={pro ? 'var(--primary)' : 'var(--success)'} strokeWidth={2.2} />
      )}
      {children}
    </li>
  );
}

// ── cancel / retention modal (F2) ──────────────────────────────────────────
const CANCEL_REASONS: readonly string[] = ['TOO PRICEY', 'NOT USING IT', 'MISSING A FEATURE', 'OTHER'];

export function CancelModal({
  open,
  onClose,
  keepsUntil,
  busy,
  onPortal,
}: {
  open: boolean;
  onClose: () => void;
  keepsUntil: number | null;
  busy: boolean;
  /** Pause offer and the actual cancellation both live in the billing portal. */
  onPortal: () => void;
}) {
  const [reason, setReason] = useState<string | null>(null);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancel Pro?"
      width={460}
      footer={
        <>
          <button type="button" style={{ ...cancelLink, marginRight: 'auto' }} onClick={busy ? undefined : onPortal}>
            Cancel anyway
          </button>
          <Button variant="ghost" onClick={onClose}>
            Keep Pro
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)' }}>
          {keepsUntil ? (
            <>You keep Pro until <b style={{ color: 'var(--text-1)' }}>{prettyDate(keepsUntil)}</b>. </>
          ) : (
            <>You keep Pro until the end of your billing period. </>
          )}
          After that: local saves stay unlimited, sync drops to 1 browser, AI search and collections pause.{' '}
          <b style={{ color: 'var(--text-1)' }}>Nothing is deleted.</b>
        </span>

        <div style={offerBlock}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={offerChip}>OFFER</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Pause instead — 3 months at $1/mo</span>
          </span>
          <span style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text-2)' }}>
            Keep sync and search alive while you’re not using it much. Cancel the pause anytime.
          </span>
          <Button fullWidth loading={busy} onClick={onPortal}>
            Pause for $1/mo
          </Button>
        </div>

        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          Why are you leaving?{' '}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>(OPTIONAL)</span>
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CANCEL_REASONS.map((r) => {
            const active = reason === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setReason(active ? null : r)}
                style={{
                  ...reasonChip,
                  borderColor: active ? 'var(--line-accent)' : 'var(--line-strong)',
                  color: active ? 'var(--accent-ink)' : 'var(--text-2)',
                }}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ── checkout notes (idiom from PricingPage) ────────────────────────────────
function CheckoutSoonNote() {
  return (
    <span style={noteInfo}>
      <Icon name="clock" size={12} color="var(--accent-ink)" />
      Checkout is opening soon — hang tight, we’ll email you the moment it’s live.
    </span>
  );
}
function CheckoutFailedNote() {
  return (
    <span style={noteDanger}>
      <Icon name="x" size={12} color="var(--danger)" />
      Something went wrong starting checkout. Please try again.
    </span>
  );
}

// ── load / error states ────────────────────────────────────────────────────
export function BillingSkeleton() {
  return (
    <div style={sectionShell} aria-busy>
      <div style={sectionHead}>
        <Skeleton width={120} height={10} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
        <Skeleton width="55%" height={14} />
        <Skeleton width="35%" height={10} />
        <Skeleton width="70%" height={12} />
        <Skeleton width={180} height={30} radius="var(--r-md)" />
      </div>
    </div>
  );
}

/** Full-panel load failure — mirrors the F3 "system error" specimen. */
export function BillingError({
  message,
  retrying,
  onRetry,
}: {
  message: string;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div style={errWrap}>
      <span style={errTile}>
        <Icon name="x" size={24} color="var(--danger)" />
      </span>
      <span style={errTitle}>Billing hit a snag</span>
      <span style={errBody}>
        Your vault is fine and keeps saving. We couldn’t load your subscription — we retry automatically, or try now.
      </span>
      <span style={errChip}>{message.toUpperCase()}</span>
      <span style={{ marginTop: 6 }}>
        <Button loading={retrying} onClick={onRetry}>
          Retry now
        </Button>
      </span>
    </div>
  );
}

// ── styles ─────────────────────────────────────────────────────────────────
const sectionShell: CSSProperties = { background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' };
const sectionHead: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--line)' };

const banner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '11px 20px',
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
  borderRadius: 12,
};
const bannerLink: CSSProperties = {
  marginLeft: 'auto',
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--danger)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const cardRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--bg)',
  border: '1px solid var(--line-strong)',
  borderRadius: 11,
  padding: '10px 13px',
};

const stateCard: CSSProperties = {
  width: 480,
  maxWidth: '100%',
  boxSizing: 'border-box',
  background: 'var(--surface-1)',
  border: '1px solid var(--line-strong)',
  borderRadius: 20,
  boxShadow: 'var(--shadow-modal)',
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const stateTitle: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, letterSpacing: '-.01em' };
const stateBody: CSSProperties = { fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' };

const monoFootnote: CSSProperties = { display: 'block', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' };

const cancelLink: CSSProperties = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--danger)',
  cursor: 'pointer',
};

const offerBlock: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg)',
  border: '1px solid var(--line-accent)',
  borderRadius: 14,
  padding: 16,
  gap: 8,
};
const offerChip: CSSProperties = {
  display: 'inline-flex',
  background: 'var(--amber)',
  color: 'var(--on-amber)',
  borderRadius: 5,
  padding: '2px 7px',
  fontFamily: 'var(--font-mono)',
  fontSize: 8,
  fontWeight: 700,
};
const reasonChip: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  background: 'transparent',
  border: '1px solid var(--line-strong)',
  borderRadius: 999,
  padding: '5px 11px',
  cursor: 'pointer',
};

const miniPlan: CSSProperties = {
  flex: 1,
  minWidth: 0,
  borderRadius: 14,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const miniPlanName: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600 };
const miniPrice: CSSProperties = {
  fontFamily: 'var(--font-display-cond)',
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: '.02em',
};
const yourPlanTag: CSSProperties = {
  display: 'inline-flex',
  background: 'var(--surface-2)',
  border: '1px solid var(--line)',
  borderRadius: 5,
  padding: '2px 7px',
  fontFamily: 'var(--font-mono)',
  fontSize: 8.5,
  color: 'var(--text-2)',
};
const featureList: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 };

const noteInfo: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  lineHeight: 1.45,
  color: 'var(--text-2)',
  background: 'var(--accent-tint)',
  border: '1px solid var(--line-accent)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};
const noteDanger: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  color: 'var(--danger)',
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};

const errWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  textAlign: 'center',
  padding: '64px 40px',
  background: 'var(--bg)',
  border: '1px solid var(--line-strong)',
  borderRadius: 16,
};
const errTile: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: 17,
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
};
const errTitle: CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, letterSpacing: '-0.025em',
};
const errBody: CSSProperties = { fontSize: 13, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 380 };
const errChip: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  color: 'var(--text-3)',
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: '6px 12px',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
